/**
 * Normalized auth error categories used by the frontend bootstrap flow.
 */
import { isRecord } from '../types.util';

export type AuthErrorKind = 'retryable' | 'reauth-required' | 'access-denied';

const RETRYABLE_AUTH_ERROR_CODES = new Set([
  'failed_to_fetch',
  'fetch_error',
  'network_error',
  'request_error',
  'temporarily_unavailable',
  'timeout',
]);

const REAUTH_REQUIRED_ERROR_CODES = new Set([
  'consent_required',
  'interaction_required',
  'invalid_grant',
  'login_required',
  'missing_refresh_token',
  'mfa_required',
]);

const ACCESS_DENIED_ERROR_CODES = new Set([
  'access_denied',
  'invalid_token',
  'unauthorized',
]);

const lowerCaseString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }

  const normalized = value.trim().toLowerCase();
  return normalized.length > 0 ? normalized : undefined;
};

const getNestedValue = (value: unknown, key: string): unknown => {
  if (!isRecord(value)) {
    return undefined;
  }

  return value[key];
};

/**
 * Extracts the most useful auth error code from Auth0-style error shapes.
 */
export const getAuthErrorCode = (error: unknown): string | undefined => {
  return (
    lowerCaseString(getNestedValue(error, 'code')) ??
    lowerCaseString(getNestedValue(error, 'error')) ??
    lowerCaseString(getNestedValue(error, 'errorCode')) ??
    lowerCaseString(getNestedValue(getNestedValue(error, 'body'), 'code')) ??
    lowerCaseString(getNestedValue(getNestedValue(error, 'body'), 'error')) ??
    lowerCaseString(getNestedValue(getNestedValue(error, 'body'), 'errorCode'))
  );
};

/**
 * Extracts a human-readable auth error message from Auth0-style error shapes.
 */
export const getAuthErrorMessage = (error: unknown): string | undefined => {
  if (error instanceof Error) {
    return error.message;
  }

  return (
    lowerCaseString(getNestedValue(error, 'message')) ??
    lowerCaseString(getNestedValue(error, 'error_description')) ??
    lowerCaseString(getNestedValue(getNestedValue(error, 'body'), 'message')) ??
    lowerCaseString(
      getNestedValue(getNestedValue(error, 'body'), 'error_description')
    )
  );
};

/**
 * Classifies raw auth/bootstrap errors into stable frontend handling buckets.
 */
export const getAuthErrorKind = (error: unknown): AuthErrorKind => {
  const code = getAuthErrorCode(error);
  const message = getAuthErrorMessage(error);

  if (code && REAUTH_REQUIRED_ERROR_CODES.has(code)) {
    return 'reauth-required';
  }

  if (code && ACCESS_DENIED_ERROR_CODES.has(code)) {
    return 'access-denied';
  }

  if (code && RETRYABLE_AUTH_ERROR_CODES.has(code)) {
    return 'retryable';
  }

  if (message?.includes('timeout') || message?.includes('network')) {
    return 'retryable';
  }

  return 'retryable';
};

/**
 * Returns whether the auth error is worth retrying automatically.
 */
export const isRetryableAuthError = (error: unknown): boolean => {
  return getAuthErrorKind(error) === 'retryable';
};
